import { AsyncPipe, DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnDestroy, inject } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatChip, MatChipSet } from '@angular/material/chips'
import { MatIcon } from '@angular/material/icon'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { Router } from '@angular/router'
import {
  DataContextBuilder,
  ElderDataToolbarComponent,
  ElderSearchBoxComponent,
  ElderSearchContextDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarContentDirective,
  Filter,
  IDataContext,
  IsoIntervalParsePipe,
  Sort,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { FilterBuilder } from '../../filter-builder'
import { WorkOrder } from '../../work-orders/work-order'
import { ProdOrderCommandService } from '../commands/prod-order-command.service'
import { OrderConfirmationState, ProdOrder } from '../prod-order'
import { ProdOrderService } from '../prod-order.service'

@Component({
  selector: 'app-prod-order-browser',
  templateUrl: './prod-order-browser.component.html',
  styleUrls: ['./prod-order-browser.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    ElderSearchBoxComponent,
    ElderSearchContextDirective,
    MatIconButton,
    MatIcon,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatSortHeader,
    MatChipSet,
    MatChip,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
    IsoIntervalParsePipe,
  ],
})
export class ProdOrderBrowserComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  public readonly data: IDataContext<ProdOrder>
  public readonly forcedFilters$ = new BehaviorSubject<Filter[]>([])
  private router = inject(Router)
  private orderService = inject(ProdOrderService)
  private orderCommandService = inject(ProdOrderCommandService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.data = DataContextBuilder.start<ProdOrder>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .autoStartOnFiltersAll(
        [FilterBuilder.FACILITY_KEY], // via forced filter
        [new Sort('createdAt', 'desc')]
      )
      .build(this.orderService)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input({ required: true })
  public set facility(facilityId: string) {
    if (facilityId) {
      const facilityFilter = FilterBuilder.facility(facilityId)
      this.forcedFilters$.next([facilityFilter])
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public isProcessable(order: ProdOrder) {
    return order.confirmationState !== OrderConfirmationState.OPEN
  }

  public openSideDetail(order: WorkOrder): void {
    this.router.navigate([{ outlets: { side: ['prod-orders', order.id] } }])
  }

  public openDetail(order: WorkOrder): void {
    if (order) {
      this.router.navigate(['app', 'prod-orders', order.id])
    }
  }

  public displayLabels(labels: string[]) {
    if (!labels) {
      return labels
    }

    labels = labels.filter((l) => !l.startsWith('vehicle'))

    if (labels && labels.length > 2) {
      return [labels[0], labels[1], '...']
    } else {
      return labels
    }
  }

  public openBatchCommand(orders: ProdOrder[]): void {
    this.orderCommandService.openBatchCommands(orders).subscribe()
  }
}
